/* eslint-disable @typescript-eslint/no-explicit-any */
import { oc } from 'ts-optchain';

import { BusinessHoursWeekdayMap, ExtendedPublicHours } from 'types';
import { allWeekdayStrings } from '@tock/shared/utils/time';
import {
  assign,
  find,
  first,
  flatMap,
  flatten,
  forEach,
  fromPairs,
  groupBy,
  includes,
  map,
  mapValues,
  reverse,
  some,
  sortBy,
  valuesIn,
  without,
} from 'es-toolkit/compat';
import { cloneDeep } from 'es-toolkit';
import { core, toJSON } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';
import EntityStateConstants from 'constants/EntityStateConstants';
import SaveButtonConstants from 'constants/SaveButtonConstants';

interface State {
  saveButtonState: string;
  forWeekday: BusinessHoursWeekdayMap;
  isInitialized: boolean;
}

const initialState: State = {
  saveButtonState: SaveButtonConstants.SAVED,
  forWeekday: fromPairs(map(allWeekdayStrings(), (day) => [day, []])),
  isInitialized: false,
};

const notSavedStates = [
  EntityStateConstants.CREATING,
  EntityStateConstants.UPDATING,
  EntityStateConstants.DELETING,
];

interface PublicHoursMap {
  [serviceType: string]: {
    endTime: string;
    startTime: string;
  };
}

const defaultValues: PublicHoursMap = {
  BREAKFAST: {
    startTime: '08:00',
    endTime: '11:00',
  },
  BRUNCH: {
    startTime: '09:00',
    endTime: '12:00',
  },
  LUNCH: {
    startTime: '11:00',
    endTime: '13:00',
  },
  DINNER: {
    startTime: '17:00',
    endTime: '23:00',
  },
};

function getSaveButtonState(state: State) {
  const needsSaving = some(flatten(valuesIn(state.forWeekday)), (publicHours) =>
    includes(notSavedStates, publicHours.state)
  );
  return needsSaving ? SaveButtonConstants.NOT_SAVED : SaveButtonConstants.SAVED;
}

function getPublicHoursById(publicHours: ExtendedPublicHours[], id: number): ExtendedPublicHours {
  return find(publicHours, (s) => s.id === id) as ExtendedPublicHours;
}

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  const forWeekday = cloneDeep(state.forWeekday);
  switch (type) {
    case AppConstants.PUBLIC_HOURS_CREATE: {
      const weekday = payload;

      let possibleServiceTypes = ['DINNER', 'LUNCH', 'BRUNCH', 'BREAKFAST'];
      possibleServiceTypes = without(
        possibleServiceTypes,
        ...map(forWeekday[weekday], (service) => service.serviceType)
      );
      const serviceType = first(possibleServiceTypes) || 'DINNER';

      const lastPublicHours = find(
        reverse(flatMap(forWeekday)),
        (publicHours) => publicHours.serviceType === serviceType
      );
      const defaultValue = defaultValues[serviceType];

      forWeekday[weekday].push({
        id: null as any,
        weekday,
        startTime: oc(lastPublicHours).startTime(defaultValue.startTime),
        endTime: oc(lastPublicHours).endTime(defaultValue.endTime),
        serviceType: serviceType as any,
        state: EntityStateConstants.CREATING,
      });
      forWeekday[weekday] = sortBy(forWeekday[weekday], 'startTime');

      return {
        ...state,
        forWeekday,
        saveButtonState: SaveButtonConstants.NOT_SAVED,
      };
    }
    case AppConstants.PUBLIC_HOURS_DELETE: {
      const deletedPublicHours = payload.publicHours;
      const index = payload.index;
      const weekday = deletedPublicHours.weekday;
      const publicHours = forWeekday[weekday][index];

      // If it has an id, we need to commit it to the db, so mark it as DELETING and that saving is needed
      if (publicHours.id) {
        publicHours.state = EntityStateConstants.DELETING;
      } else {
        // Otherwise, just remove it from the list and update the save button state accordingly
        forWeekday[weekday].splice(index, 1);
      }
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: publicHours.id
          ? SaveButtonConstants.NOT_SAVED
          : getSaveButtonState(newState),
      };
    }

    case AppConstants.PUBLIC_HOURS_UPDATE: {
      const publicHoursChange = payload.publicHoursChange;
      const weekday = publicHoursChange.weekday;
      const index = payload.index;
      const publicHours = forWeekday[weekday][index];

      if (publicHoursChange.serviceType) {
        const lastPublicHours = find(reverse(flatMap(forWeekday)), {
          serviceType: publicHoursChange.serviceType,
        }) as core.PublicHours;
        const defaultValue = defaultValues[publicHoursChange.serviceType];

        assign(publicHours, {
          startTime: oc(lastPublicHours).startTime(defaultValue.startTime),
          endTime: oc(lastPublicHours).endTime(defaultValue.endTime),
        });
      }

      assign(publicHours, publicHoursChange);

      if (publicHours.state === EntityStateConstants.SAVED) {
        publicHours.state = EntityStateConstants.UPDATING;
        state.saveButtonState = SaveButtonConstants.NOT_SAVED;
      }
      return {
        ...state,
        forWeekday,
      };
    }

    case AppConstants.PUBLIC_HOURS_CREATE_DONE: {
      const publicHours = toJSON<core.PublicHours>(payload);
      const publicHoursForDay = forWeekday[publicHours.weekday];
      let pendingPublicHours = find(
        publicHoursForDay,
        (ph) =>
          ph.id === null &&
          publicHours.startTime === ph.startTime &&
          publicHours.endTime === ph.endTime &&
          publicHours.serviceType === ph.serviceType
      );
      pendingPublicHours = assign(pendingPublicHours, publicHours);
      pendingPublicHours.state = EntityStateConstants.SAVED;
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.PUBLIC_HOURS_DELETE_DONE: {
      const deletedPublicHours = context.deletedPublicHours;
      const weekday: Weekday = deletedPublicHours.weekday;
      const publicHours = getPublicHoursById(forWeekday[weekday], deletedPublicHours.id);

      // Sanity check / allow for rollbacks
      if (publicHours.state === EntityStateConstants.DELETING) {
        forWeekday[weekday] = without(forWeekday[weekday], publicHours);
      }
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.PUBLIC_HOURS_UPDATE_DONE: {
      const updatedPublicHours = toJSON<core.PublicHours>(payload);
      const weekday = updatedPublicHours.weekday;
      const publicHours = getPublicHoursById(forWeekday[weekday], updatedPublicHours.id);
      publicHours.state = EntityStateConstants.SAVED;
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.PUBLIC_HOURS_CREATE_STARTING:
    case AppConstants.PUBLIC_HOURS_DELETE_STARTING:
    case AppConstants.PUBLIC_HOURS_UPDATE_STARTING: {
      return {
        ...state,
        saveButtonState: SaveButtonConstants.SAVING,
      };
    }
    case AppConstants.PUBLIC_HOURS_CREATE_FAILED:
    case AppConstants.PUBLIC_HOURS_DELETE_FAILED:
    case AppConstants.PUBLIC_HOURS_UPDATE_FAILED: {
      return {
        ...state,
        saveButtonState: SaveButtonConstants.NOT_SAVED,
      };
    }
    case AppConstants.PUBLIC_HOURS_FETCH_ALL_STARTING: {
      return {
        ...state,
        isInitialized: state.isInitialized,
      };
    }
    case AppConstants.PUBLIC_HOURS_FETCH_ALL_DONE: {
      const publicHoursList = payload.map((publicHoursMessage: core.PublicHours) => {
        const publicHours = toJSON<core.PublicHours>(publicHoursMessage);
        (publicHours as ExtendedPublicHours).state = EntityStateConstants.SAVED;
        return publicHours;
      });
      assign(
        forWeekday,
        groupBy(publicHoursList, (publicHours) => publicHours.weekday)
      );
      forEach(Object.keys(forWeekday), (weekday) => {
        forWeekday[weekday] = sortBy(forWeekday[weekday], 'startTime');
      });
      const newState = {
        ...state,
        forWeekday,
        isInitialized: true,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.PUBLIC_HOURS_INITIALIZE_FROM_SERVICES: {
      const servicesForWeekday: BusinessHoursWeekdayMap = payload;
      const fromServicesForWeekday: BusinessHoursWeekdayMap = mapValues(
        servicesForWeekday,
        (services): ExtendedPublicHours[] => {
          return map(services, (service): ExtendedPublicHours => {
            return {
              ...service,
              id: null as any,
              state: EntityStateConstants.CREATING,
            };
          });
        }
      );
      const newState = {
        ...state,
        forWeekday: fromServicesForWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    default:
      return state;
  }
}
